import type { Ref } from 'react';
import React, { useCallback, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { NetworkRequestState } from 'c-data-layer';
import type { ChartReportProps } from 'c-reports/Types';
import { ReportType } from 'c-reports/Types';
import { Translate, useCommonTranslation } from 'c-translation';
import { formatCompactLower, formatNumber, secondsToHms } from 'c-lib';
import ReportBarChart from '../ReportBarChart';
import ReportComposedChart from '../ReportComposedChart';
import ReportLoadFailed from '../ReportLoadFailed';
import ReportLineChart from '../ReportLineChart';
import ReportPieChart from '../ReportPieChart';

type Props = ChartReportProps<any> & {
    type: ReportType;
    loadingState: NetworkRequestState;
    /** Re-runs a failed request; shown as Retry beside the error. */
    onRetry?: () => void;

    showValueLabels?: boolean;
    valueLabelType?: 'number' | 'percent';
    percentDataKey?: string;
    valuesArePercent?: boolean;
};

const ReportChartWrapper = (
    {
        type,
        loadingState,
        onRetry,
        yAxisTickFormatterAsTime,
        parts,
        data,

        showValueLabels,
        valueLabelType = 'number',
        percentDataKey,
        valuesArePercent,

        ...props
    }: Props,
    ref: Ref<any>,
) => {
    const t = useCommonTranslation();

    const tickFormatter = useCallback(
        value => {
            if (Number.isFinite(value)) {
                if (yAxisTickFormatterAsTime) {
                    const { h, m, s } = secondsToHms(value);
                    if (h > 0) return t('Reporting.ticks.hoursLabel', { count: formatNumber(h) });
                    if (m > 0) return t('Reporting.ticks.minutesLabel', { count: formatNumber(m) });
                    return t('Reporting.ticks.secondsLabel', { count: formatNumber(s ?? 0) });
                }
                return formatCompactLower(value);
            }
            return value;
        },
        [yAxisTickFormatterAsTime, t],
    );

    const anyLabelOverrides = useMemo(
        () => parts.filter(p => p.labelOverride && p.labelOverride !== p.dataKey).length > 0,
        [parts],
    );

    const overridenData = useMemo(() => {
        if (!anyLabelOverrides) return data;
        return data.map(row => {
            const next = { ...row };
            parts.forEach(p => {
                if (p.labelOverride && p.labelOverride !== p.dataKey) {
                    next[p.labelOverride] = next[p.dataKey];
                    delete next[p.dataKey];
                }
            });
            return next;
        });
    }, [data, parts, anyLabelOverrides]);

    const overridenParts = useMemo(() => {
        if (!anyLabelOverrides) return parts;
        return parts.map(p => ({ ...p, dataKey: p.labelOverride ?? p.dataKey }));
    }, [parts, anyLabelOverrides]);

    const chartComponentProps: ChartReportProps<any> & {
        showValueLabels?: boolean;
        valueLabelType?: 'number' | 'percent';
        percentDataKey?: string;
        valuesArePercent?: boolean;
    } = useMemo(
        () => ({
            ...props,
            hideLegend: props.hideLegend ?? false,
            hideBrush: props.hideBrush ?? false,
            hideDots: props.hideDots ?? false,
            hideTooltip: props.hideTooltip ?? false,
            parts: overridenParts,
            data: overridenData,
            yAxisTickFormatter: tickFormatter,
            showValueLabels,
            valueLabelType,
            percentDataKey,
            valuesArePercent,
        }),
        [
            overridenParts,
            overridenData,
            tickFormatter,
            props,
            showValueLabels,
            valueLabelType,
            percentDataKey,
            valuesArePercent,
        ],
    );

    const failed = loadingState === NetworkRequestState.Error;
    const noData = useMemo(() => {
        if (loadingState === NetworkRequestState.Success) {
            return Array.isArray(chartComponentProps.data) && chartComponentProps.data.length === 0;
        }
        return false;
    }, [loadingState, chartComponentProps.data]);

    const chartChild = useMemo(() => {
        if (type === ReportType.Line)
            return <ReportLineChart {...(chartComponentProps as any)} ref={ref} />;
        if (type === ReportType.Bar)
            return <ReportBarChart {...(chartComponentProps as any)} ref={ref} />;
        if (type === ReportType.Mixed)
            return <ReportComposedChart {...(chartComponentProps as any)} ref={ref} />;
        if (type === ReportType.Pie)
            return <ReportPieChart {...(chartComponentProps as any)} ref={ref} />;
        return null;
    }, [type, chartComponentProps, ref]);

    return (
        <>
            {failed && <ReportLoadFailed onRetry={onRetry} />}
            {noData && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                        color: 'grey.500',
                    }}
                >
                    <Typography variant="h2">
                        <Translate path="Reporting.noData" />
                    </Typography>
                </Box>
            )}
            {!noData && !failed && (
                <Box height="100%" width="100%">
                    {chartChild}
                </Box>
            )}
        </>
    );
};

export default React.forwardRef(ReportChartWrapper);
